import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEmailCredentials, EmailCredential } from "@/hooks/useEmailCredentials";
import { useEmailSignatures } from "@/hooks/useEmailSignatures";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { SignatureEditor } from "@/components/SignatureEditor";
import {
  Mail,
  CheckCircle2,
  XCircle,
  Loader2,
  Trash2,
  Shield,
  Pen,
  Users,
  Send,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

interface EmailCredentialsManagerProps {
  isAdmin: boolean;
}

type CredentialScope = "team" | "personal";
type SignatureScope = "team" | "agent";

export const EmailCredentialsManager = ({ isAdmin }: EmailCredentialsManagerProps) => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { teamCredentials, userCredentials, loading, refetch } = useEmailCredentials();
  const { agents } = useTeamAgents();

  // Credential scope (team vs personal credentials)
  const [credScope, setCredScope] = useState<CredentialScope>(isAdmin ? "team" : "personal");
  // Signature scope (team default vs agent)
  const [sigScope, setSigScope] = useState<SignatureScope>("team");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const {
    teamSignature,
    agentSignature,
    loading: sigLoading,
    refetch: refetchSignatures,
  } = useEmailSignatures(sigScope === "agent" ? selectedAgentId : null);

  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Signature state
  const [signatureHtml, setSignatureHtml] = useState("");
  const [isSavingSignature, setIsSavingSignature] = useState(false);

  // Display name form state
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");

  // Get current credential for the active credential scope
  const currentCredential: EmailCredential | null =
    credScope === "team"
      ? teamCredentials[0] || null
      : userCredentials[0] || null;

  // Handle Google OAuth redirect result
  useEffect(() => {
    const oauthResult = searchParams.get("email_oauth");
    if (oauthResult === "success") {
      toast({
        title: "Gmail Connected",
        description: "Your Gmail account has been connected successfully.",
      });
      refetch();
    } else if (oauthResult === "error") {
      const message = searchParams.get("message") || "Unknown error";
      toast({
        title: "Gmail Connection Failed",
        description: message,
        variant: "destructive",
      });
    }
    if (oauthResult) {
      searchParams.delete("email_oauth");
      searchParams.delete("message");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // Populate form from existing credential
  useEffect(() => {
    if (currentCredential) {
      setFromName(currentCredential.fromName || "");
      setFromEmail(currentCredential.fromEmail || "");
    } else {
      setFromName("");
      setFromEmail("");
    }
  }, [currentCredential?.id, credScope]);

  // Populate signature from existing data when scope or agent changes
  useEffect(() => {
    if (sigScope === "team") {
      setSignatureHtml(teamSignature?.signatureHtml ?? "");
    } else {
      setSignatureHtml(agentSignature?.signatureHtml ?? "");
    }
  }, [sigScope, selectedAgentId, teamSignature?.signatureHtml, agentSignature?.signatureHtml]);

  const handleSaveSignature = async () => {
    setIsSavingSignature(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-email-signature`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            scope: sigScope,
            signatureHtml,
            ...(sigScope === "agent" && selectedAgentId ? { agentId: selectedAgentId } : {}),
          }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to save signature");
      }

      toast({ title: "Saved", description: "Email signature saved." });
      await refetchSignatures();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save signature",
        variant: "destructive",
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const body = {
        scope: credScope,
        provider: "google_oauth",
        fromName: fromName || null,
        fromEmail: fromEmail || null,
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-email-credentials`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to save");
      }

      toast({ title: "Saved", description: "Display settings saved." });
      await refetch();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = async () => {
    const recipient = testEmail.trim();
    if (!recipient) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipient)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    setIsTesting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-report-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            recipientEmail: recipient,
            subject: "North Reports - Test Email",
            htmlBody: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                <h2 style="color: #1a1a1a;">Email Connection Verified</h2>
                <p style="color: #4a4a4a; line-height: 1.6;">
                  This is a test email from <strong>North Reports</strong> to verify your email setup is working correctly.
                </p>
                <p style="color: #4a4a4a; line-height: 1.6;">
                  If you received this email, your Gmail connection is set up properly and you can now send report emails directly from the platform.
                </p>
                <hr style="border: none; border-top: 1px solid #e5e5e5; margin: 20px 0;" />
                <p style="color: #888; font-size: 12px;">Sent via North Reports email integration</p>
              </div>
            `,
          }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to send test email");
      }

      toast({
        title: "Test Email Sent",
        description: `A test email was sent to ${recipient}`,
      });
      setShowTestDialog(false);
    } catch (err) {
      toast({
        title: "Test Failed",
        description: err instanceof Error ? err.message : "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-email-credentials`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ credentialId: deleteTargetId }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || "Failed to delete");
      }

      toast({ title: "Deleted", description: "Email credentials removed." });
      setFromName("");
      setFromEmail("");
      await refetch();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteTargetId(null);
    }
  };

  const handleConnectGmail = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast({
        title: "Not configured",
        description: "Google OAuth is not configured for this environment.",
        variant: "destructive",
      });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const redirectUri = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-oauth-callback`;

    // Build state parameter including return URL and the exact redirect URI
    // so the callback uses the same URI for the token exchange
    const state = btoa(JSON.stringify({
      userId: session.user.id,
      scope: credScope,
      timestamp: Date.now(),
      returnUrl: window.location.origin,
      redirectUri,
    }));

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/userinfo.email",
      access_type: "offline",
      prompt: "consent",
      state,
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            <div>
              <CardTitle>Email Sending</CardTitle>
              <CardDescription>
                Connect your Gmail or Google Workspace account to send report emails directly from the platform.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Credential Scope Tabs (Admin only) */}
          {isAdmin && (
            <div className="flex gap-2">
              <Button
                variant={credScope === "team" ? "default" : "outline"}
                size="sm"
                onClick={() => setCredScope("team")}
              >
                <Shield className="h-4 w-4 mr-1" />
                Team Default
              </Button>
              <Button
                variant={credScope === "personal" ? "default" : "outline"}
                size="sm"
                onClick={() => setCredScope("personal")}
              >
                <Mail className="h-4 w-4 mr-1" />
                My Personal
              </Button>
            </div>
          )}

          {/* Current Status */}
          {currentCredential && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">
                    Gmail: {currentCredential.googleEmail}
                  </span>
                  {currentCredential.isVerified ? (
                    <Badge variant="default" className="bg-green-600 text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      <XCircle className="h-3 w-3 mr-1" />
                      Not Verified
                    </Badge>
                  )}
                </div>
                {currentCredential.fromEmail && (
                  <p className="text-xs text-muted-foreground mt-1">
                    From: {currentCredential.fromName ? `${currentCredential.fromName} <${currentCredential.fromEmail}>` : currentCredential.fromEmail}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDeleteTargetId(currentCredential.id);
                  setShowDeleteDialog(true);
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          )}

          {/* Google OAuth Section */}
          {(
            <div className="space-y-4">
              {currentCredential?.provider === "google_oauth" ? (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    Your Gmail account is connected. Emails will be sent via Gmail API.
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gmail-from-name">Display Name</Label>
                      <Input
                        id="gmail-from-name"
                        placeholder="e.g. Jane Smith or Acme Realty"
                        value={fromName}
                        onChange={(e) => setFromName(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        The name recipients see in their inbox (instead of just your email username).
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gmail-from-email" className="text-muted-foreground">From Email</Label>
                      <Input
                        id="gmail-from-email"
                        type="email"
                        value={currentCredential.googleEmail || ""}
                        disabled
                      />
                      <p className="text-xs text-muted-foreground">
                        Gmail only allows sending from your connected address. To send from a different address, configure a "Send As" alias in your Google Workspace admin.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      disabled={isSaving}
                      size="sm"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Display Settings"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setTestEmail(currentCredential?.googleEmail || "");
                        setShowTestDialog(true);
                      }}
                      size="sm"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Test Email
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Connect your Gmail or Google Workspace account to send emails directly through Google's servers. This is the easiest setup option.
                  </p>
                  <Button variant="outline" onClick={handleConnectGmail}>
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Help text */}
          <div className="text-xs text-muted-foreground border-t pt-4 space-y-1">
            <p>Emails are sent through Google's servers, appearing from your Gmail or Google Workspace address.</p>
            {isAdmin && credScope === "team" && (
              <p>Team default credentials are used by all team members who haven't set up their own.</p>
            )}
          </div>

          {/* Email Signature */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Pen className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Email Signatures</Label>
            </div>

            {/* Signature Scope Tabs */}
            {isAdmin && (
              <div className="flex gap-2">
                <Button
                  variant={sigScope === "team" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSigScope("team")}
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Team Default
                </Button>
                <Button
                  variant={sigScope === "agent" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSigScope("agent")}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Agent Signatures
                </Button>
              </div>
            )}

            {sigScope === "agent" ? (
              <div className="space-y-3">
                {agents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No agents found. Add agents in Settings &rarr; Team Roster to create agent-specific signatures.
                  </p>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Select Agent</Label>
                      <Select
                        value={selectedAgentId || ""}
                        onValueChange={(val) => setSelectedAgentId(val)}
                      >
                        <SelectTrigger className="w-full max-w-xs">
                          <SelectValue placeholder="Choose an agent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {agents.map((agent) => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedAgentId && (
                      <div className="space-y-2">
                        <SignatureEditor
                          value={signatureHtml}
                          onChange={setSignatureHtml}
                          disabled={sigLoading}
                        />
                        <Button
                          onClick={handleSaveSignature}
                          disabled={isSavingSignature}
                          size="sm"
                        >
                          {isSavingSignature ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            "Save Agent Signature"
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <SignatureEditor
                  value={signatureHtml}
                  onChange={setSignatureHtml}
                  disabled={sigLoading}
                />
                <Button
                  onClick={handleSaveSignature}
                  disabled={isSavingSignature}
                  size="sm"
                >
                  {isSavingSignature ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Team Signature"
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Email Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Enter the email address where you'd like to receive the test email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="test-email-address">Recipient Email</Label>
            <Input
              id="test-email-address"
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTestEmail();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)} size="sm">
              Cancel
            </Button>
            <Button onClick={handleTestEmail} disabled={isTesting || !testEmail.trim()} size="sm">
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Credentials</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the email credentials. You won't be able to send report emails from the platform until new credentials are configured.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
